import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useScreeningFeedback } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/hooks/useScreeningFeedback'
import { useSubmitScreeningFeedback } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/hooks/useSubmitScreeningFeedback'

export default function ScreeningFeedback({
  screeningReportData,
  screeningReportTemplateData,
  bookedEventInfo,
  mentorProfileData,
  showTherapyRecommendation = false,
  showDiagnosisFeedback = false,
  closeDialog,
  afterSubmitFeedback,
}) {
  const { isSubmitting, handleSubmit } = useSubmitScreeningFeedback(closeDialog)
  const { feedbackObject, handleRatingChange, handleRadioChange, handleNotesChange } =
    useScreeningFeedback(screeningReportData, mentorProfileData)

  async function handleSubmitFeedback() {
    await handleSubmit(feedbackObject, showTherapyRecommendation, showDiagnosisFeedback)
    afterSubmitFeedback()
  }

  return (
    <>
      <DialogTitle>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
          Screening Feedback (Optional)
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ minWidth: '500px', pb: 2 }}>
        <Stack spacing={2}>
          {/* Rating Sections */}
          <Stack>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Quality of Report
              </Typography>
              <Rating
                value={feedbackObject.qualityRating}
                onChange={(event, newValue) => handleRatingChange('qualityRating', newValue)}
                size="large"
              />
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Usefulness for Diagnosis
              </Typography>
              <Rating
                value={feedbackObject.usefulnessRating}
                onChange={(event, newValue) => handleRatingChange('usefulnessRating', newValue)}
                size="large"
              />
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Completeness of Information
              </Typography>
              <Rating
                value={feedbackObject.completenessRating}
                onChange={(event, newValue) => handleRatingChange('completenessRating', newValue)}
                size="large"
              />
            </Box>
          </Stack>

          {/* Diagnosis Question */}
          {showDiagnosisFeedback && (
            <FormControl component="fieldset" sx={{ mt: 1 }}>
              <FormLabel component="legend">
                <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  Did the screener&apos;s diagnosis seem appropriate?
                </Typography>
              </FormLabel>
              <RadioGroup
                value={feedbackObject.diagnosisAgreement}
                onChange={(event) => handleRadioChange('diagnosisAgreement', event.target.value)}
              >
                <FormControlLabel
                  value="AGREE"
                  control={<Radio sx={{ py: '4px' }} />}
                  label="Agree"
                />
                <FormControlLabel
                  value="DISAGREE"
                  control={<Radio sx={{ py: '4px' }} />}
                  label="Disagree"
                />
                <FormControlLabel
                  value="NO_DIAGNOSIS_GIVEN"
                  control={<Radio sx={{ py: '4px' }} />}
                  label="No diagnosis given"
                />
              </RadioGroup>
            </FormControl>
          )}

          {/* Therapy Recommendation Question */}
          {showTherapyRecommendation && (
            <FormControl component="fieldset" sx={{ mt: 1 }}>
              <FormLabel component="legend">
                <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  Was therapy recommendation justified?
                </Typography>
              </FormLabel>
              <RadioGroup
                value={feedbackObject.therapyRecommendationAgreement}
                onChange={(event) =>
                  handleRadioChange('therapyRecommendationAgreement', event.target.value)
                }
              >
                <FormControlLabel
                  value="AGREE"
                  control={<Radio sx={{ py: '4px' }} />}
                  label="Agree"
                />
                <FormControlLabel
                  value="DISAGREE"
                  control={<Radio sx={{ py: '4px' }} />}
                  label="Disagree"
                />
                <FormControlLabel
                  value="NOT_SUGGESTED"
                  control={<Radio sx={{ py: '4px' }} />}
                  label="Not suggested"
                />
              </RadioGroup>
            </FormControl>
          )}

          {/* Additional Notes */}
          <TextField
            multiline
            rows={4}
            placeholder="Additional notes (optional)..."
            value={feedbackObject.comments}
            onChange={handleNotesChange}
            variant="outlined"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={closeDialog}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitFeedback}
          sx={{ px: 4 }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </DialogActions>
    </>
  )
}
